import { networkLabel } from "src/shared/enums/networkColors"

/*
    "Total on hover" for the grouped-by-network day charts (MPN-1530 §3.5 and §4.5).

    Four charts want the same thing: hovering a day should show each network's value AND
    the combined total for that day. Apex's default shared tooltip lists the series but
    never adds them up, so the reader is left summing five numbers in their head to answer
    "how many impressions did we get on the 14th", which is the question the chart is for.

    One builder rather than four copies, because §4.5 asks for exactly the pattern §3.5
    describes and the two would otherwise drift in wording and rounding.

    Built as an HTML string because that is Apex's `tooltip.custom` contract. Series names
    come from the chart config, which is built from our own network list, so they are not
    user input; values are numbers. Nothing here interpolates anything a client typed.
*/

export type TooltipValueFormat = "number" | "currency"

function formatValue(value: number, format: TooltipValueFormat): string {
  if (format === "currency") {
    return `$${value.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`
  }
  return Math.round(value).toLocaleString()
}

function formatDay(raw: unknown): string {
  // The day-series charts plot [epochMs, value]; the category charts plot strings.
  if (typeof raw === "number") {
    return new Date(raw).toLocaleDateString(undefined, {
      day: "numeric",
      month: "short",
      year: "numeric",
    })
  }
  return String(raw ?? "")
}

/**
 * An Apex `tooltip.custom` that lists each series for the hovered point and totals them.
 *
 * Series contributing nothing on the hovered day are dropped: a five-network campaign
 * where two were dark reads better as three rows than as three rows and two zeros.
 * The total still counts them, since adding zero changes nothing.
 */
export function totalOnHoverTooltip(
  format: TooltipValueFormat = "number",
  /*
      How to present a series name. Defaults to the network labeller, since most charts
      using this are split by network. The stacked conversions chart (§3.2) is split by
      journey step, whose names are already display strings and should pass through
      untouched rather than be run through a network map that happens not to break them.
  */
  labelFor: (name: string) => string = networkLabel,
) {
  return ({ series, dataPointIndex, w }: any): string => {
    const names: string[] = w?.globals?.seriesNames ?? []
    const colors: string[] = w?.globals?.colors ?? []

    const rows: string[] = []
    let total = 0

    for (let i = 0; i < series.length; i += 1) {
      const raw = series[i]?.[dataPointIndex]
      const value = typeof raw === "number" && Number.isFinite(raw) ? raw : 0
      total += value
      if (value === 0) continue
      rows.push(
        `<div class="mon-tip__row">
           <span class="mon-tip__dot" style="background:${colors[i] ?? "#94a3b8"}"></span>
           <span class="mon-tip__name">${labelFor(names[i] ?? "")}</span>
           <span class="mon-tip__val">${formatValue(value, format)}</span>
         </div>`,
      )
    }

    const label = formatDay(
      w?.globals?.categoryLabels?.[dataPointIndex] ??
        w?.globals?.labels?.[dataPointIndex] ??
        w?.globals?.seriesX?.[0]?.[dataPointIndex],
    )

    return `
      <div class="mon-tip">
        <div class="mon-tip__head">${label}</div>
        ${rows.join("") || '<div class="mon-tip__empty">Nothing delivered</div>'}
        <div class="mon-tip__total">
          <span class="mon-tip__name">Total</span>
          <span class="mon-tip__val">${formatValue(total, format)}</span>
        </div>
      </div>`
  }
}
